import { groupBy } from './Helper';

/**
 * Set of functions that provide data transformation and manipulations for the
 * miEdge welfare API https://github.com/miEdge/welfare_api
 */

/**
 * given the output from the /welfare/business_lines api returns an array that summarizes
 * each lob type and provides a count of the plans labelled with that type
 *
 * @param {*} welfareLinesOfBusiness
 */

export const summarizeWelfareBusinessLinesToLobs = (welfareLinesOfBusiness) => {
  const benefitPlansLobList = [];

  if (!welfareLinesOfBusiness || welfareLinesOfBusiness.length < 1) {
    console.info('welfareLinesOfBusiness not provided');
    return benefitPlansLobList;
  }

  welfareLinesOfBusiness.forEach((lob) => {
    const lineTypes = groupBy(lob.miedge_categories, 'display_name');
    Object.keys(lineTypes).forEach((lineType) => {
      const filteredLobs = benefitPlansLobList.filter((lob) => {
        return lineType === lob.value;
      });
      if (filteredLobs.length < 1) {
        //add a new record
        benefitPlansLobList.push({
          value: lineType,
          count: 1,
        });
      } else {
        filteredLobs[0].count += 1;
      }
    });
    if (lob.other_contract === 'Y') {
      const otherLobs = benefitPlansLobList.filter((lob) => lob.value === 'Other');
      if (otherLobs.length < 1) {
        benefitPlansLobList.push({
          value: 'Other',
          count: 1,
        });
      } else {
        otherLobs[0].count += 1;
      }
    }
  });

  // Sort alphabetically
  benefitPlansLobList.sort((a, b) => {
    if (a.value < b.value) {
      return -1;
    }
    if (a.value > b.value) {
      return 1;
    }
    return 0;
  });

  benefitPlansLobList.forEach((lob) => {
    lob.label = `${lob.value} (${lob.count})`;
  });

  return benefitPlansLobList;
};

/**
 * given the output from the /welfare/business_lines api returns an array that summarizes
 * each carrier and provides a count of the plans provided by that carrier
 *
 * @param {*} welfareLinesOfBusiness
 */

export const summarizeWelfareBusinessLinesToCarriers = (welfareLinesOfBusiness) => {
  if (!welfareLinesOfBusiness) {
    console.info('welfareLinesOfBusiness not provided');
    return [];
  }
  // First group the data by carrier
  let carriers = groupBy(welfareLinesOfBusiness, 'carrier_name_normalized');

  carriers = groupBy(welfareLinesOfBusiness, 'carrier_name');

  // Map to an array
  const benefitsPlansCarriersList = Object.keys(carriers).map((carrier) => {
    return {
      label: `${carrier} (${carriers[carrier].length})`,
      value: carrier,
      count: carriers[carrier].length,
    };
  });

  // Sort alphabetically
  benefitsPlansCarriersList.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  return benefitsPlansCarriersList;
};

/**
 * given the output from the /welfare/business_lines api returns an array that summarizes
 * each employer and provides a count of the plans with that employer
 *
 * @param {*} welfareLinesOfBusiness
 */

export const summarizeWelfareBusinessLinesToEmployers = (welfareLinesOfBusiness) => {
  if (!welfareLinesOfBusiness) {
    console.info('welfareLinesOfBusiness not provided');
    return [];
  }
  // First group the data by employer
  const employers = groupBy(welfareLinesOfBusiness, 'sponsor_dfe_name');

  // Map to an array
  const benefitsPlansEmployersList = Object.keys(employers).map((employer) => {
    return {
      label: `${employer} (${employers[employer].length})`,
      value: employer,
      count: employers[employer].length,
    };
  });

  // Sort alphabetically
  benefitsPlansEmployersList.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  return benefitsPlansEmployersList;
};

/**
 * given the output from the /welfare/business_lines api returns an array that summarizes
 * each broker and provides a count of the plans provided by that broker
 *
 * @param {*} welfareLinesOfBusiness
 */

export const summarizeWelfareBusinessLinesToBrokers = (welfareLinesOfBusiness) => {
  const benefitPlansBrokersList = [];
  let brokerNotProvidedCount = 0;
  if (!welfareLinesOfBusiness || welfareLinesOfBusiness.length < 1) {
    console.info('welfareLinesOfBusiness not provided');
    return benefitPlansBrokersList;
  }

  welfareLinesOfBusiness.forEach((lob) => {
    if (lob.brokers && lob.brokers.length > 0) {
      lob.brokers.forEach((broker) => {
        if (benefitPlansBrokersList.filter((item) => item.value === broker.name).length === 0) {
          benefitPlansBrokersList.push({
            label: broker.name,
            value: broker.name,
            count: 0,
          });
        }
      });
    } else {
      brokerNotProvidedCount++;
    }
  });

  // Add the counts
  welfareLinesOfBusiness.forEach((lob) => {
    benefitPlansBrokersList.forEach((item) => {
      for (let i = 0; i < lob.brokers.length; i++) {
        if (item.value === lob.brokers[i].name) {
          item.count++;
          item.label = `${item.value} (${item.count})`;
          break;
        }
      }
    });
  });

  // Sort alphabetically
  benefitPlansBrokersList.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  // Add an item for Broker Not Provided if we have any
  if (brokerNotProvidedCount > 0) {
    benefitPlansBrokersList.push({
      label: `Broker Not Provided (${brokerNotProvidedCount})`,
      value: 'Broker Not Provided',
      count: brokerNotProvidedCount,
    });
  }
  return benefitPlansBrokersList;
};

/**
 * given the output from the /welfare/service_providers api returns an array that summarizes
 * each provider and provides a count of the plans provided by that provider
 *
 * @param {*} welfareServiceProviders
 */

export const summarizeWelfareServiceProvidersToProviders = (welfareServiceProviders) => {
  if (!welfareServiceProviders) {
    console.info('welfareServiceProviders not provided');
    return [];
  }
  // First group the data by provider
  const providers = groupBy(welfareServiceProviders, 'name');

  // Map to an array
  const serviceProvidersList = Object.keys(providers).map((provider) => {
    return {
      label: `${provider} (${providers[provider].length})`,
      value: provider,
      count: providers[provider].length,
    };
  });

  // Sort alphabetically
  serviceProvidersList.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  return serviceProvidersList;
};

/**
 * given the output from the /welfare/service_providers api returns an array that summarizes
 * each employer and provides a count of the plans for that employer
 *
 * @param {*} welfareServiceProviders
 */

export const summarizeWelfareServiceProvidersToEmployers = (welfareServiceProviders) => {
  if (!welfareServiceProviders) {
    console.info('welfareServiceProviders not provided');
    return [];
  }
  // First group the data by employer
  const employers = groupBy(welfareServiceProviders, 'sponsor_dfe_name');

  // Map to an array
  const serviceEmployersList = Object.keys(employers).map((employer) => {
    return {
      label: `${employer} (${employers[employer].length})`,
      value: employer,
      count: employers[employer].length,
    };
  });

  // Sort alphabetically
  serviceEmployersList.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  return serviceEmployersList;
};

/**
 * Enhanced Filtering of EB Plans  https://github.com/miEdge/app.newEdge/issues/833
 * given the output from the /welfare/business_lines api returns an array that summarizes
 * each broker and provides a count of the plans provided by that broker (Normalized)
 *
 * @param {*} welfareLinesOfBusiness
 */

export const summarizeWelfareBusinessLinesToBrokersNormalized = (welfareLinesOfBusiness) => {
  const benefitPlansBrokersList = [];
  let brokerNotProvidedCount = 0;
  if (!welfareLinesOfBusiness || welfareLinesOfBusiness.length < 1) {
    console.info('welfareLinesOfBusiness not provided');
    return benefitPlansBrokersList;
  }

  welfareLinesOfBusiness.forEach((lob) => {
    if (lob.brokers && lob.brokers.length > 0) {
      lob.brokers.forEach((broker) => {
        if (
          benefitPlansBrokersList.filter((item) => item.value === broker.name_normalized).length ===
          0
        ) {
          benefitPlansBrokersList.push({
            label: broker.name_normalized,
            value: broker.name_normalized,
            count: 0,
          });
        }
      });
    } else {
      brokerNotProvidedCount++;
    }
  });

  // Add the counts
  welfareLinesOfBusiness.forEach((lob) => {
    benefitPlansBrokersList.forEach((item) => {
      for (let i = 0; i < lob.brokers.length; i++) {
        if (item.value === lob.brokers[i].name_normalized) {
          item.count++;
          item.label = `${item.value} (${item.count})`;
          break;
        }
      }
    });
  });

  // Sort alphabetically
  benefitPlansBrokersList.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  // Add an item for Broker Not Provided if we have any
  if (brokerNotProvidedCount > 0) {
    benefitPlansBrokersList.push({
      label: `Broker Not Provided (${brokerNotProvidedCount})`,
      value: 'Broker Not Provided',
      count: brokerNotProvidedCount,
    });
  }
  return benefitPlansBrokersList;
};

/**
 * given the output from the /welfare/business_lines api returns an array that summarizes
 * each carrier and provides a count of the plans provided by that carrier (Normalized)
 *
 * @param {*} welfareLinesOfBusiness
 */

export const summarizeWelfareBusinessLinesToCarriersNormalized = (welfareLinesOfBusiness) => {
  if (!welfareLinesOfBusiness) {
    console.info('welfareLinesOfBusiness not provided');
    return [];
  }
  // First group the data by carrier
  const carriers = groupBy(welfareLinesOfBusiness, 'carrier_name_normalized');

  // Map to an array
  const benefitsPlansCarriersList = Object.keys(carriers).map((carrier) => {
    return {
      label: `${carrier} (${carriers[carrier].length})`,
      value: carrier,
      count: carriers[carrier].length,
    };
  });

  // Sort alphabetically
  benefitsPlansCarriersList.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  return benefitsPlansCarriersList;
};
